import { App, createApp } from 'vue'
import { createPinia, Pinia } from 'pinia'
import * as fabric from 'fabric'
import { useFabricStore, useTemplatesStore, useMainStore } from '@/store'
import { FabricCanvas } from '@/app/fabricCanvas'
import useCanvas from '@/views/Canvas/useCanvas'
import useHandleCreate from '@/hooks/useHandleCreate'
import useCanvasExport from '@/hooks/useCanvasExport'
import useHandleElement from '@/hooks/useHandleElement'
import { PDFDocument } from 'pdf-lib'
import { ElementNames, RightStates } from '@/types/elements'
import { nanoid } from 'nanoid'
import { WorkSpaceDrawType } from '@/configs/canvas'

export interface PDFEditorConfig {
  container: HTMLElement | string
  width?: number
  height?: number
  initialPDF?: string | ArrayBuffer
  theme?: 'light' | 'dark'
  wasmUrl?: string // URL to wasm file
  locale?: 'en' | 'vi' | 'zh'
  features?: {
    text?: boolean
    image?: boolean
    shape?: boolean
    qrcode?: boolean
    barcode?: boolean
    signature?: boolean
    watermark?: boolean
  }
  onReady?: () => void
  onExport?: (data: Blob) => void
  onError?: (error: Error) => void
}

export interface EditorAPI {
  // Canvas control
  getCanvas: () => FabricCanvas | null
  setZoom: (zoom: number) => void
  getZoom: () => number
  fitToScreen: () => void
  
  // Element creation
  addText: (text?: string, options?: any) => void
  addImage: (url: string) => void
  addShape: (type: string, options?: any) => void
  addQRCode: (content: string, options?: any) => void
  addBarCode: (content: string, options?: any) => void
  addSignature: (imageData: string) => void
  addWatermark: (text: string, options?: any) => void
  
  // Element manipulation
  deleteSelected: () => void
  duplicateSelected: () => void
  selectAll: () => void
  clearSelection: () => void
  
  // PDF operations
  loadPDF: (file: File | ArrayBuffer | string) => Promise<void>
  getPageCount: () => number
  goToPage: (pageNumber: number) => void
  addPage: () => void
  deletePage: (pageNumber: number) => void
  
  // Export
  exportToPDF: (options?: ExportOptions) => Promise<Blob>
  exportToPNG: (options?: ExportOptions) => Promise<Blob>
  exportToJPEG: (options?: ExportOptions) => Promise<Blob>
  
  // Lifecycle
  destroy: () => void
  mount: () => void
  unmount: () => void
}

export interface ExportOptions {
  quality?: number
  dpi?: number
  format?: 'png' | 'jpeg' | 'pdf'
  pages?: 'all' | 'current' | number[]
}

/**
 * PDF Editor SDK - Main class for integrating PDF editor into any application
 * 
 * @example
 * ```javascript
 * const editor = new PDFEditorSDK({
 *   container: '#editor-container',
 *   width: 800,
 *   height: 600,
 *   onReady: () => {
 *     console.log('Editor ready!')
 *   }
 * })
 * 
 * // Load a PDF
 * await editor.loadPDF('/path/to/file.pdf')
 * 
 * // Add text
 * editor.addText('Hello World', { fontSize: 24 })
 * 
 * // Export to PDF
 * const blob = await editor.exportToPDF()
 * ```
 */
export class PDFEditorSDK implements EditorAPI {
  private config: PDFEditorConfig
  private container: HTMLElement | null = null
  private pinia: Pinia | null = null
  private fabricCanvas: FabricCanvas | null = null
  private isInitialized: boolean = false
  private canvasElement: HTMLCanvasElement | null = null
  private wrapperElement: HTMLDivElement | null = null

  constructor(config: PDFEditorConfig) {
    this.config = {
      width: 800,
      height: 600,
      theme: 'light',
      locale: 'en',
      features: {
        text: true,
        image: true,
        shape: true,
        qrcode: true,
        barcode: true,
        signature: true,
        watermark: true,
      },
      ...config,
    }

    this.init()
  }

  /**
   * Initialize the SDK
   */
  private async init() {
    try {
      // Get container element
      if (typeof this.config.container === 'string') {
        this.container = document.querySelector(this.config.container)
      } else {
        this.container = this.config.container
      }

      if (!this.container) {
        throw new Error('Container element not found')
      }

      // Create Pinia instance
      this.pinia = createPinia()

      // Create canvas wrapper and element
      this.createCanvasElements()

      // Initialize Fabric canvas
      await this.initializeCanvas()

      this.isInitialized = true

      // Call onReady callback
      if (this.config.onReady) {
        this.config.onReady()
      }
    } catch (error) {
      this.handleError(error as Error)
    }
  }

  /**
   * Create canvas DOM elements
   */
  private createCanvasElements() {
    if (!this.container) return

    // Create wrapper
    this.wrapperElement = document.createElement('div')
    this.wrapperElement.className = 'pdf-editor-wrapper'
    this.wrapperElement.style.width = `${this.config.width}px`
    this.wrapperElement.style.height = `${this.config.height}px`
    this.wrapperElement.style.position = 'relative'
    this.wrapperElement.style.overflow = 'hidden'

    // Create canvas
    this.canvasElement = document.createElement('canvas')
    this.canvasElement.id = 'pdf-editor-canvas-' + nanoid(6)
    
    this.wrapperElement.appendChild(this.canvasElement)
    this.container.appendChild(this.wrapperElement)
  }

  /**
   * Initialize Fabric.js canvas
   */
  private async initializeCanvas() {
    if (!this.canvasElement || !this.pinia) return

    const fabricStore = useFabricStore(this.pinia)
    const templatesStore = useTemplatesStore(this.pinia)
    const mainStore = useMainStore(this.pinia)

    // Create Fabric canvas instance
    this.fabricCanvas = new FabricCanvas(this.canvasElement, {
      fireRightClick: true,
      stopContextMenu: true,
      controlsAboveOverlay: true,
      preserveObjectStacking: true,
    }) as FabricCanvas

    // Set canvas in store
    fabricStore.canvas = this.fabricCanvas
    fabricStore.wrapperRef = this.wrapperElement
    fabricStore.canvasRef = this.canvasElement

    // Initialize fonts
    await mainStore.getFonts()
  }

  /**
   * Get the Fabric canvas instance
   */
  getCanvas(): FabricCanvas | null {
    return this.fabricCanvas
  }

  /**
   * Get the center point of the workspace or canvas
   */
  private getCenterPoint(): fabric.Point {
    const canvas = this.fabricCanvas
    if (!canvas) return new fabric.Point(0, 0)
    
    // Try to find the workspace object
    const workSpaceDraw = canvas.getObjects().find((item: any) => item.id === WorkSpaceDrawType)
    
    if (workSpaceDraw) {
      return new fabric.Point(
        workSpaceDraw.left + workSpaceDraw.width / 2,
        workSpaceDraw.top + workSpaceDraw.height / 2
      )
    }
    
    // Fallback to canvas center
    return (canvas as any).getCenterPoint()
  }

  /**
   * Set canvas zoom level
   */
  setZoom(zoom: number): void {
    if (!this.fabricCanvas) return
    const fabricStore = useFabricStore(this.pinia!)
    fabricStore.zoom = zoom
    
    const center = this.getCenterPoint()
    ;(this.fabricCanvas as any).zoomToPoint(center, zoom)
    ;(this.fabricCanvas as any).requestRenderAll()
  }

  /**
   * Get current zoom level
   */
  getZoom(): number {
    if (!this.fabricCanvas) return 1
    return (this.fabricCanvas as any).getZoom()
  }

  /**
   * Fit canvas to screen
   */
  fitToScreen(): void {
    if (!this.fabricCanvas || !this.wrapperElement) return
    
    const containerWidth = this.wrapperElement.offsetWidth
    const containerHeight = this.wrapperElement.offsetHeight
    
    // Calculate zoom to fit
    const zoom = Math.min(
      containerWidth / ((this.fabricCanvas as any).getWidth() || 1),
      containerHeight / ((this.fabricCanvas as any).getHeight() || 1)
    )
    
    this.setZoom(zoom * 0.9) // 90% to leave some padding
  }

  /**
   * Add text element to canvas
   */
  addText(text: string = 'Double click to edit', options: any = {}): void {
    if (!this.pinia) return
    const { createTextElement } = useHandleCreate()
    createTextElement(options.fontSize || 24, options.textStyle || 'transverse', options.hollow || false, text)
  }

  /**
   * Add image to canvas
   */
  addImage(url: string): void {
    if (!this.pinia) return
    const { createImageElement } = useHandleCreate()
    createImageElement(url)
  }

  /**
   * Add shape to canvas
   */
  addShape(type: string, options: any = {}): void {
    if (!this.pinia) return
    const { createPathElement } = useHandleCreate()
    // You can extend this with different shape types
    createPathElement(type, options.left, options.top)
  }

  /**
   * Add QR code to canvas
   */
  addQRCode(content: string, options: any = {}): void {
    if (!this.pinia) return
    const { createQRCodeElement } = useHandleCreate()
    // Generate QR code URL first, then create element
    // This is simplified - actual implementation may vary
    createQRCodeElement('', options, content)
  }

  /**
   * Add barcode to canvas
   */
  addBarCode(content: string, options: any = {}): void {
    if (!this.pinia) return
    const { createBarCodeElement } = useHandleCreate()
    createBarCodeElement('', content, options)
  }

  /**
   * Add signature image
   */
  addSignature(imageData: string): void {
    this.addImage(imageData)
  }

  /**
   * Add watermark
   */
  addWatermark(text: string, options: any = {}): void {
    // Create semi-transparent text as watermark
    this.addText(text, {
      ...options,
      fontSize: options.fontSize || 48,
      opacity: options.opacity || 0.3,
    })
  }

  /**
   * Delete selected elements
   */
  deleteSelected(): void {
    if (!this.pinia) return
    const { removeElement } = useHandleElement()
    removeElement()
  }

  /**
   * Duplicate selected elements
   */
  duplicateSelected(): void {
    if (!this.pinia) return
    const { copyElement, pasteElement } = useHandleElement()
    copyElement()
    setTimeout(() => pasteElement(), 50)
  }

  /**
   * Select all elements
   */
  selectAll(): void {
    if (!this.fabricCanvas) return
    const selection = new fabric.ActiveSelection(
      (this.fabricCanvas as any).getObjects().filter((obj: any) => obj.selectable),
      { canvas: this.fabricCanvas }
    )
    ;(this.fabricCanvas as any).setActiveObject(selection)
    ;(this.fabricCanvas as any).renderAll()
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    if (!this.fabricCanvas) return
    ;(this.fabricCanvas as any).discardActiveObject()
    ;(this.fabricCanvas as any).renderAll()
  }

  /**
   * Load a PDF file
   */
  /**
   * Load a PDF file
   */
  async loadPDF(file: File | ArrayBuffer | string): Promise<void> {
    if (!this.pinia) return

    try {
      let pdfBytes: ArrayBuffer

      if (file instanceof File) {
        pdfBytes = await file.arrayBuffer()
      } else if (typeof file === 'string') {
        // Fetch from URL
        const response = await fetch(file)
        pdfBytes = await response.arrayBuffer()
      } else {
        pdfBytes = file
      }

      // Dynamic import to avoid bundling issues if possible, or just standard import
      // We need to use the logic from src/api/file/index.ts
      
      console.log('Loading PDF with MuPDF...')
      // @ts-ignore
      const { default: mupdfFactory } = await import('mupdf-js/dist/libmupdf')
      
      let wasmBinary: ArrayBuffer | undefined;
      if (this.config.wasmUrl) {
        try {
          console.log('[SDK] Fetching custom WASM from:', this.config.wasmUrl);
          const response = await fetch(this.config.wasmUrl);
          if (!response.ok) {
             throw new Error(`Failed to load WASM: ${response.status} ${response.statusText}`);
          }
          wasmBinary = await response.arrayBuffer();
          console.log('[SDK] Custom WASM loaded, size:', wasmBinary.byteLength);
        } catch (e) {
          console.error('[SDK] Error fetching WASM:', e);
          throw e;
        }
      }

      // Initialize directly with the factory to pass options
      const mupdfInstance = await mupdfFactory({
        wasmBinary,
        locateFile: (path: string) => {
           // Fallback if wasmBinary is not used for some reason
           if (path.endsWith('.wasm') && this.config.wasmUrl) {
             console.log('[SDK] locateFile returning user wasmUrl');
             return this.config.wasmUrl;
           }
           return path;
        }
      })
      
      // Manually wrap the instance (replicating wrapMuPdf from library)
      const mupdf = {
        ...mupdfInstance,
        load: (fileData: Uint8Array, name = 'doc.pdf') => {
           mupdfInstance.FS.writeFile(name, fileData);
           return mupdfInstance.openDocument(name);
        }
      }
      
      const arrayBuf = new Uint8Array(pdfBytes)
      const doc = mupdf.load(arrayBuf)
      const pageCount = mupdf.countPages(doc)
      
      console.log(`PDF loaded. Pages: ${pageCount}`)
      
      const templatesStore = useTemplatesStore(this.pinia)
      await templatesStore.clearTemplates()
      
      const { loadSVGFromString } = await import('@/extension/parser/loadSVGFromString')
      const { propertiesToInclude, WorkSpaceDrawData } = await import('@/configs/canvas')
      
      // Process each page
      const newTemplates: any[] = []
      
      for (let i = 0; i < pageCount; i++) {
        // Draw page as SVG
        const svg = mupdf.drawPageAsSVG(doc, i + 1)
        
        // Parse SVG to Fabric objects
        // Simplified SVG optimization (skipping heavy svgo for now to ensure compatible build)
        // If needed, we can add svgo back
        
        const content = await loadSVGFromString(svg)
        console.log(`Page ${i+1}: Local loadSVGFromString result:`, content)
        
        const options = content.options
        const svgObjects: any[] = []
        
        if (content.objects && content.objects.length > 0) {
          content.objects.slice(0, 1000).forEach((ele: any) => {
            svgObjects.push(ele.toObject(propertiesToInclude))
          })
        } else {
          console.warn(`Page ${i+1}: No objects found in SVG content`)
        }
        
        console.log(`Page ${i+1}: Parsed ${svgObjects.length} objects`)
        
        WorkSpaceDrawData.width = options.width
        WorkSpaceDrawData.height = options.height
        
        const template = {
          id: nanoid(10),
          version: '6.12',
          zoom: 1,
          width: options.width,
          height: options.height,
          clip: 2,
          objects: [WorkSpaceDrawData, ...svgObjects],
          workSpace: {
            fillType: 0,
            left: 0,
            top: 0,
            angle: 0,
            scaleX: 1,
            scaleY: 1,
          },
          order: i
        }
        
        newTemplates.push(template)
      }
      
      console.log('Adding templates to store:', newTemplates)
      
      // Add templates to store
      await templatesStore.addTemplate(newTemplates)
      await templatesStore.setTemplateIndex(0)
      
      console.log('CALLING renderTemplate...')
      await templatesStore.renderTemplate()
      console.log('FINISHED renderTemplate')
      
      console.log('PDF pages rendered and added to store')

    } catch (error) {
      this.handleError(error as Error)
    }
  }

  /**
   * Get page count
   */
  getPageCount(): number {
    if (!this.pinia) return 0
    const templatesStore = useTemplatesStore(this.pinia)
    return templatesStore.templates.length
  }

  /**
   * Go to specific page
   */
  goToPage(pageNumber: number): void {
    if (!this.pinia) return
    const templatesStore = useTemplatesStore(this.pinia)
    templatesStore.setTemplateIndex(pageNumber - 1)
  }

  /**
   * Add a new page
   */
  addPage(): void {
    if (!this.pinia) return
    const templatesStore = useTemplatesStore(this.pinia)
    templatesStore.addTemplate()
  }

  /**
   * Delete a page
   */
  deletePage(pageNumber: number): void {
    if (!this.pinia) return
    const templatesStore = useTemplatesStore(this.pinia)
    templatesStore.deleteTemplate(pageNumber - 1)
  }

  /**
   * Export to PDF
   */
  async exportToPDF(options: ExportOptions = {}): Promise<Blob> {
    if (!this.pinia) throw new Error('SDK not initialized')
    
    const { exportPDF } = useCanvasExport()
    const rangeType = options.pages || 'all'
    
    // Export PDF (pass false to prevent auto-download and get the blob)
    // @ts-ignore
    const blob = await exportPDF({ value: rangeType }, false)
    
    if (blob) return blob
    
    return new Blob([], { type: 'application/pdf' })
  }

  /**
   * Export to PNG
   */
  async exportToPNG(options: ExportOptions = {}): Promise<Blob> {
    if (!this.pinia) throw new Error('SDK not initialized')
    
    const { exportImage } = useCanvasExport()
    exportImage('png', options.quality || 1, options.dpi || 72)
    
    // This is a simplified return - actual implementation would return the blob
    return new Blob([], { type: 'image/png' })
  }

  /**
   * Export to JPEG
   */
  async exportToJPEG(options: ExportOptions = {}): Promise<Blob> {
    if (!this.pinia) throw new Error('SDK not initialized')
    
    const { exportImage } = useCanvasExport()
    exportImage('jpeg', options.quality || 0.9, options.dpi || 72)
    
    // This is a simplified return - actual implementation would return the blob
    return new Blob([], { type: 'image/jpeg' })
  }

  /**
   * Mount the editor
   */
  mount(): void {
    if (!this.container || !this.wrapperElement) return
    if (!this.container.contains(this.wrapperElement)) {
      this.container.appendChild(this.wrapperElement)
    }
  }

  /**
   * Unmount the editor
   */
  unmount(): void {
    if (!this.container || !this.wrapperElement) return
    if (this.container.contains(this.wrapperElement)) {
      this.container.removeChild(this.wrapperElement)
    }
  }

  /**
   * Destroy the editor and cleanup
   */
  destroy(): void {
    // Dispose canvas
    if (this.fabricCanvas) {
      ;(this.fabricCanvas as any).dispose()
      this.fabricCanvas = null
    }

    // Remove DOM elements
    if (this.wrapperElement && this.container) {
      this.container.removeChild(this.wrapperElement)
    }

    this.wrapperElement = null
    this.canvasElement = null
    this.pinia = null
    this.isInitialized = false
  }

  /**
   * Handle errors
   */
  private handleError(error: Error): void {
    console.error('[PDFEditorSDK]', error)
    if (this.config.onError) {
      this.config.onError(error)
    }
  }
}
